import type {
  CreateAssetCategoryInput,
  CreateAssetSubcategoryInput,
  CreateAssetBrandInput,
  CreateAssetStatusInput,
  BulkCreateAssetInput,
  UpdateAssetCategoryInput,
  UpdateAssetSubcategoryInput,
  UpdateAssetBrandInput,
  UpdateAssetStatusInput,
  UpdateAssetInput,
  ListAssetsQuery,
  DataResponse,
  PaginatedResponse,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Response types ──────────────────────────────────────────────────────────

export interface AssetCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  subcategory_count: number;
}

export interface AssetSubcategory {
  id: string;
  category_id: string;
  category_name: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AssetBrand {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AssetStatus {
  id: string;
  name: string;
  code: string;
  color: string;
  display_order: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AssetListItem {
  id: string;
  asset_code: string;
  category: { id: string; name: string } | null;
  subcategory: { id: string; name: string } | null;
  brand: { id: string; name: string } | null;
  model_name: string | null;
  status: { id: string; name: string; code: string; color: string };
  assigned_to: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
  warranty_available: boolean;
  warranty_end_date: string | null;
  purchase_date: string | null;
  created_at: string;
  photo_url: string | null;
  photo_doc_id: string | null;
  /** First active invoice/bill; only the paginated list sends these. */
  invoice_doc_id?: string | null;
  invoice_mime_type?: string | null;
}

export interface AssetDocument {
  id: string;
  asset_id: string;
  document_type: 'invoice' | 'photo';
  original_name: string;
  file_path: string;
  /** Null on rows imported by the asset sync — fall back to the file extension. */
  mime_type: string | null;
  size_bytes: number;
  is_active: boolean;
  created_at: string;
}

export interface AssetAssignmentHistory {
  id: string;
  asset_id: string;
  assigned_to: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
  assigned_by: { id: string; name: string } | null;
  assigned_at: string;
  unassigned_at: string | null;
  notes: string | null;
}

export interface AssetDetail extends AssetListItem {
  notes: string | null;
  documents: AssetDocument[];
  assignment_history: AssetAssignmentHistory[];
}

/** Created assets in code order. */
export interface BulkCreateAssetsResult {
  count: number;
  assets: { id: string; asset_code: string }[];
}

export interface AssetStats {
  total: number;
  by_status: Array<{ status_id: string; status_name: string; status_code: string; color: string; count: number }>;
  by_category: Array<{ category_id: string; category_name: string; count: number }>;
  under_warranty: number;
  warranty_expiring_soon: number;
  unassigned: number;
  recent: AssetListItem[];
}

// ── Service ─────────────────────────────────────────────────────────────────

/** Grace period before releasing a download's blob URL, so the browser can commit the file. */
const REVOKE_DELAY_MS = 60_000;

export const assetsService = {
  // Stats / config
  async getStats(): Promise<AssetStats> {
    const res = await api.get<DataResponse<AssetStats>>('/assets/stats');
    return res.data.data;
  },

  async getCodePrefix(): Promise<string> {
    const res = await api.get<DataResponse<{ value: string }>>('/assets/config/asset_code_prefix');
    return res.data.data.value;
  },

  async setCodePrefix(value: string): Promise<void> {
    await api.patch('/assets/config/asset_code_prefix', { value });
  },

  // Categories
  async listCategories(): Promise<AssetCategory[]> {
    const res = await api.get<DataResponse<AssetCategory[]>>('/assets/categories');
    return res.data.data;
  },

  async createCategory(input: CreateAssetCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/assets/categories', input);
    return res.data.data;
  },

  async updateCategory(id: string, input: UpdateAssetCategoryInput): Promise<void> {
    await api.patch(`/assets/categories/${id}`, input);
  },

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/assets/categories/${id}`);
  },

  // Subcategories
  async listSubcategories(categoryId?: string): Promise<AssetSubcategory[]> {
    const params = categoryId ? { category_id: categoryId } : {};
    const res = await api.get<DataResponse<AssetSubcategory[]>>('/assets/subcategories', { params });
    return res.data.data;
  },

  async createSubcategory(input: CreateAssetSubcategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/assets/subcategories', input);
    return res.data.data;
  },

  async updateSubcategory(id: string, input: UpdateAssetSubcategoryInput): Promise<void> {
    await api.patch(`/assets/subcategories/${id}`, input);
  },

  async deleteSubcategory(id: string): Promise<void> {
    await api.delete(`/assets/subcategories/${id}`);
  },

  // Brands
  async listBrands(): Promise<AssetBrand[]> {
    const res = await api.get<DataResponse<AssetBrand[]>>('/assets/brands');
    return res.data.data;
  },

  async createBrand(input: CreateAssetBrandInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/assets/brands', input);
    return res.data.data;
  },

  async updateBrand(id: string, input: UpdateAssetBrandInput): Promise<void> {
    await api.patch(`/assets/brands/${id}`, input);
  },

  async deleteBrand(id: string): Promise<void> {
    await api.delete(`/assets/brands/${id}`);
  },

  // Statuses
  async listStatuses(): Promise<AssetStatus[]> {
    const res = await api.get<DataResponse<AssetStatus[]>>('/assets/statuses');
    return res.data.data;
  },

  async createStatus(input: CreateAssetStatusInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/assets/statuses', input);
    return res.data.data;
  },

  async updateStatus(id: string, input: UpdateAssetStatusInput): Promise<void> {
    await api.patch(`/assets/statuses/${id}`, input);
  },

  async deleteStatus(id: string): Promise<void> {
    await api.delete(`/assets/statuses/${id}`);
  },

  // Assets CRUD
  async listAssets(query: Partial<ListAssetsQuery> = {}): Promise<PaginatedResponse<AssetListItem>> {
    const res = await api.get<PaginatedResponse<AssetListItem>>('/assets', { params: query });
    return res.data;
  },

  /**
   * Downloads the filtered asset register as CSV. Sends the same params as
   * listAssets — the backend ignores page/limit and returns every match.
   */
  async exportCsv(query: Partial<ListAssetsQuery> = {}): Promise<void> {
    const res = await api.get('/assets/export/csv', { params: query, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv' }));
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assets-${today}.csv`;
    // anchor must be in the document for the click to register in some browsers
    document.body.appendChild(a);
    a.click();
    a.remove();
    // defer revocation so a slow browser still has the blob when it commits the file
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  },

  async listMyAssets(query: Partial<Omit<ListAssetsQuery, 'assigned_to_user_id'>> = {}): Promise<PaginatedResponse<AssetListItem>> {
    const res = await api.get<PaginatedResponse<AssetListItem>>('/assets/my', { params: query });
    return res.data;
  },

  async getMyAsset(id: string): Promise<AssetDetail> {
    const res = await api.get<DataResponse<AssetDetail>>(`/assets/my/${id}`);
    return res.data.data;
  },

  async getAsset(id: string): Promise<AssetDetail> {
    const res = await api.get<DataResponse<AssetDetail>>(`/assets/${id}`);
    return res.data.data;
  },

  // One request: N identical assets plus their shared files, all-or-nothing on the server.
  async bulkCreateAssets(
    input: BulkCreateAssetInput,
    files: { invoices: File[]; photos: File[] },
  ): Promise<BulkCreateAssetsResult> {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(input)); // first, ahead of the files
    for (const file of files.invoices) formData.append('invoice', file);
    for (const file of files.photos) formData.append('photo', file);
    const res = await api.post<DataResponse<BulkCreateAssetsResult>>(
      '/assets/bulk-create',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data.data;
  },

  async updateAsset(id: string, input: UpdateAssetInput): Promise<void> {
    await api.patch(`/assets/${id}`, input);
  },

  async deleteAsset(id: string): Promise<void> {
    await api.delete(`/assets/${id}`);
  },

  async updateAssetStatus(id: string, statusId: string): Promise<void> {
    await api.patch(`/assets/${id}/status`, { status_id: statusId });
  },

  // Documents
  async uploadDocument(
    assetId: string,
    file: File,
    type: 'invoice' | 'photo',
  ): Promise<AssetDocument> {
    const formData = new FormData();
    formData.append(type, file);
    const res = await api.post<DataResponse<AssetDocument>>(
      `/assets/${assetId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data.data;
  },

  async deleteDocument(docId: string): Promise<void> {
    await api.delete(`/assets/documents/${docId}`);
  },

  async downloadDocument(docId: string, filename: string, basePath = '/assets/documents'): Promise<void> {
    const res = await api.get<Blob>(`${basePath}/${docId}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};
